/**
 * Timeline Playback — Post-Tick forensische Analyse + Living City Renderer.
 * Loads timeline.json and renders animated city map with event log.
 */

import { fixture } from '../data/fixture';
import { t } from '../i18n';
import type { RunnerDTO } from '../data/fixture';
import {
  loadTimeline, TimelineOutput, EventRecord, CombatRecord,
  EventCategory, EVENT_CATEGORIES, categoryOf,
} from '../data/timeline';
import { PlaybackMap } from './playback-map';
import { RunnerSidebar } from './runner-sidebar';
import { portraitHTML, hydratePortraits } from '../scene/PortraitEngine';

// ── Unified event type for display ────────────────────────────────────────

interface DisplayEvent {
  tick: number;
  runner: string;
  action: string;
  sector: string;
  nodeId: string;
  result: string;
  detail: string;
  category: EventCategory;
  /** Original server record so the detail panel can surface the full payload. */
  source: EventRecord | CombatRecord;
  sourceKind: 'event' | 'combat';
}

/** Filter: 'all' or one of the 7 spec §9.5 event categories. */
type FilterCategory = 'all' | EventCategory;

// ── Helpers ───────────────────────────────────────────────────────────────

/** Map entity_id "p0_h2" → runner name from fixture. */
function resolveRunnerName(entityId: string): string {
  const match = entityId.match(/^p\d+_h(\d+)$/);
  if (!match) return entityId;
  const hoodIdx = parseInt(match[1], 10);
  const runners = fixture?.player?.runners;
  if (runners && hoodIdx < runners.length) return runners[hoodIdx].name;
  return entityId;
}

/** Map node_id → sector_id. Node IDs follow "S-XX_nYYY" pattern. */
function resolveNodeSector(nodeId: string): string {
  const sectorMatch = nodeId.match(/^(S-\d+)/);
  return sectorMatch ? sectorMatch[1] : nodeId;
}

/** Map entity_id "p0_h2" → RunnerDTO from fixture. */
function resolveEntityToRunner(entityId: string): RunnerDTO | null {
  const match = entityId.match(/^p\d+_h(\d+)$/);
  if (!match) return null;
  const idx = parseInt(match[1], 10);
  const runners = fixture?.player?.runners;
  if (runners && idx < runners.length) return runners[idx];
  return null;
}

function resultBadge(r: string): string {
  const map: Record<string, [string, string]> = {
    SUCCESS:      ['SUCCESS', 'var(--green)'],
    CRIT_SUCCESS: ['CRIT',    'var(--cyan)'],
    FAIL:         ['FAIL',    'var(--red)'],
    CRIT_FAIL:    ['CRIT FAIL','var(--magenta)'],
    ARRESTED:     ['ARREST',  'var(--amber)'],
    attacker_win: ['ATK WIN', 'var(--green)'],
    defender_win: ['DEF WIN', 'var(--red)'],
    draw:         ['DRAW',    'var(--amber)'],
  };
  const [label, color] = map[r] ?? ['?', 'var(--text-lo)'];
  return `<span style="font-family:var(--mono);font-size:11px;padding:2px 7px;border-radius:3px;background:${color}22;border:1px solid ${color}55;color:${color}">${label}</span>`;
}

function fmtMin(t: number): string {
  const h = Math.floor(t / 60).toString().padStart(2, '0');
  const m = (t % 60).toString().padStart(2, '0');
  return `${h}:${m}`;
}

/** Convert timeline data into unified display events. Each event is
 *  categorised per spec §9.5; unknown event types fall back to
 *  'begegnungen' (catch-all for street-level actions) so unmapped
 *  types never silently vanish from the log. */
function buildDisplayEvents(tl: TimelineOutput): DisplayEvent[] {
  const events: DisplayEvent[] = [];

  for (const e of tl.events) {
    events.push({
      tick: e.tick,
      runner: resolveRunnerName(e.actor),
      action: e.event_type,
      sector: resolveNodeSector(e.node_id),
      nodeId: e.node_id,
      result: e.result,
      detail: e.detail || formatEventDetail(e),
      category: categoryOf(e.event_type) ?? 'begegnungen',
      source: e,
      sourceKind: 'event',
    });
  }

  for (const c of tl.combats) {
    events.push({
      tick: c.tick_start,
      runner: resolveRunnerName(c.attacker_id),
      action: 'COMBAT',
      sector: resolveNodeSector(c.node_id),
      nodeId: c.node_id,
      result: c.outcome,
      detail: formatCombatDetail(c),
      category: 'kampf',
      source: c,
      sourceKind: 'combat',
    });
  }

  events.sort((a, b) => a.tick - b.tick);
  return events;
}

function formatEventDetail(e: EventRecord): string {
  const parts: string[] = [];
  if (e.delta_cash) parts.push(`${e.delta_cash > 0 ? '+' : ''}$${Math.round(e.delta_cash).toLocaleString('de-DE')}`);
  if (e.delta_heat) parts.push(`heat ${e.delta_heat > 0 ? '+' : ''}${e.delta_heat}`);
  return parts.join(' · ') || e.event_type;
}

function formatCombatDetail(c: CombatRecord): string {
  const att = resolveRunnerName(c.attacker_id);
  const def = resolveRunnerName(c.defender_id);
  const dur = c.tick_end - c.tick_start;
  return `${att} vs ${def} — ${dur} min (${fmtMin(c.tick_start)}–${fmtMin(c.tick_end)})`;
}

// ── Render ────────────────────────────────────────────────────────────────

export function renderPlayback(): HTMLElement {
  const root = document.createElement('div');
  root.className = 'screen';
  root.style.cssText = 'display:flex;flex-direction:column;height:100%;overflow:hidden';
  const style = document.createElement('style');
  style.textContent = `
    .pb-layout { display:flex; gap:12px; min-height:0; flex:1; }
    .pb-map-wrap { flex:1; min-width:300px; position:relative; overflow:hidden; background:#0a0c14; border:1px solid var(--border); border-radius:3px; }
    .pb-map-wrap canvas { display:block; width:100%; height:100%; }
    .pb-sidebar { width:420px; flex-shrink:0; overflow-y:auto; display:flex; flex-direction:column; gap:12px; }
    /* .pb-table → shared .dtable (marker kept); past/current row states stay local */
    .pb-table tr.past td { opacity: 0.5; }
    .pb-table tr.current td { background: rgba(var(--ac),0.06); }
    .pb-controls { display:flex; align-items:center; gap:12px; padding:12px 0; flex-wrap:wrap; }
    /* .pb-btn → shared .btn (+ .active); marker kept for JS */
    .pb-slider { flex:1; accent-color:var(--cyan); cursor:pointer; min-width:200px; }
    .pb-time { font-family:var(--mono); font-size:13px; color:var(--cyan); min-width:50px; }
    .pb-filter { display:flex; gap:8px; }
    /* .pb-filter-btn → shared .btn.sm (+ .active, marker for JS); .pb-loading/.pb-empty → shared .empty(.loading) */
    .pb-entity-legend { display:flex; flex-wrap:wrap; gap:8px; padding:6px 12px; font-family:var(--mono); font-size:12px; color:var(--text-lo); }
    .pb-entity-dot { width:8px; height:8px; border-radius:50%; background:var(--cyan); display:inline-block; margin-right:3px; }
    /* Spec §9.7 forensic side-panel — fixed-position, inside pb-map-wrap */
    .pb-detail { position:absolute; top:8px; right:8px; width:340px; max-height:calc(100% - 16px); background:rgba(10,12,20,0.96); border:1px solid var(--cyan-dim); border-radius:4px; box-shadow:0 4px 16px rgba(0,0,0,0.6); display:flex; flex-direction:column; z-index:1000; font-family:var(--mono); font-size:12px; }
    .pb-detail-head { display:flex; align-items:center; justify-content:space-between; padding:10px 12px; border-bottom:1px solid var(--cyan-dim); background:rgba(var(--ac),0.06); }
    .pb-detail-head span { color:var(--cyan); letter-spacing:0.08em; text-transform:uppercase; font-size:11px; }
    .pb-detail-close { background:none; border:1px solid var(--border); color:var(--text-mid); cursor:pointer; padding:2px 8px; font-family:var(--mono); font-size:14px; border-radius:2px; }
    .pb-detail-close:hover { color:var(--red); border-color:var(--red); }
    .pb-detail-body { padding:10px 12px; overflow-y:auto; }
    .pb-detail-section { margin-bottom:10px; }
    .pb-detail-label { color:var(--text-lo); font-size:10px; text-transform:uppercase; letter-spacing:0.08em; margin-bottom:2px; }
    .pb-detail-value { color:var(--text-hi); font-size:12px; line-height:1.4; }
    .pb-detail-kv { display:flex; justify-content:space-between; padding:2px 0; border-bottom:1px dashed rgba(255,255,255,0.04); }
    .pb-detail-kv:last-child { border-bottom:none; }
    .pb-detail-kv-k { color:var(--text-lo); }
    .pb-detail-kv-v { color:var(--text-hi); font-family:var(--mono); }
    /* Fog of War dimming — events outside player's visibility */
    tr.pb-row-fogged td { opacity:0.35; }
    tr.pb-row-fogged td:last-child::after { content:' \\1F507'; color:var(--text-lo); font-size:10px; }
    /* Clickable event rows */
    tr.pb-row-clickable { cursor:pointer; }
    tr.pb-row-clickable:hover td { background:rgba(var(--ac),0.08); }
    @media (max-width:1024px) {
      .pb-sidebar { width:min(420px, 45vw); }
    }
  `;
  root.appendChild(style);

  root.innerHTML += `
    <div class="screen-title">${t('playback.title', { cycle: String(fixture?.player?.cycle ?? '?') })}</div>
    <div id="pb-content" class="pb-loading empty loading">${t('playback.loading')}</div>
  `;

  loadTimeline().then(tl => {
    const content = root.querySelector('#pb-content') as HTMLElement;
    if (!tl || (tl.events.length === 0 && tl.combats.length === 0 && tl.movements.length === 0)) {
      content.className = 'pb-empty empty';
      content.textContent = t('playback.no_timeline');
      return;
    }
    renderTimelineUI(content, tl);
  });

  return root;
}

function renderTimelineUI(container: HTMLElement, tl: TimelineOutput): void {
  const allEvents = buildDisplayEvents(tl);
  let filter: FilterCategory = 'all';
  let currentTick = 0;
  let playing = false;
  let speed = 1;
  let animFrame = 0;
  let lastWallTime = 0;

  // Spec §9.4: 1x · 5x · 10x · 60x. 60× = 1 game-hour per wall-second,
  // letting the player scrub through a full 24h cycle in ~24 seconds.
  const SPEEDS = [1, 5, 10, 60];

  // ── Create PlaybackMap instance ──
  const pbMap = new PlaybackMap();
  pbMap.setTimeline(tl);
  pbMap.loadGraph();

  container.className = '';
  container.style.cssText = 'display:flex;flex-direction:column;flex:1;min-height:0';
  container.innerHTML = `
    <div class="panel" style="margin-bottom:12px;flex-shrink:0">
      <div class="panel-title">${t('playback.controls', { player: tl.meta.player })}</div>

      <div class="pb-controls">
        <button class="pb-btn btn" data-pb="start">|&lt; Start</button>
        <button class="pb-btn btn" data-pb="play">&#9654; Play</button>
        <button class="pb-btn btn" data-pb="pause">&#9646;&#9646; Pause</button>
        <button class="pb-btn btn" data-pb="end">End &gt;|</button>
        <div style="width:1px; height:24px; background:var(--border)"></div>
        <span style="font-size:12px; color:var(--text-lo)">Speed:</span>
        ${SPEEDS.map(s => '<button class="pb-btn btn pb-speed' + (s === 1 ? ' active' : '') + '" data-speed="' + s + '">' + s + '\u00d7</button>').join('')}
        <div style="width:1px; height:24px; background:var(--border)"></div>
        <span style="font-size:12px; color:var(--text-lo)">Skip:</span>
        <button class="pb-btn btn" data-pb="skip-combat">&#8594; Kampf</button>
        <button class="pb-btn btn" data-pb="skip-fail">&#8594; Fehler</button>
        <div style="width:1px; height:24px; background:var(--border)"></div>
        <button class="pb-btn btn" data-pb="export" title="${t('playback.export_hint') ?? 'timeline_day_N.json herunterladen'}">&#8595; ${t('playback.export') ?? 'Export'}</button>
      </div>

      <div style="display:flex; align-items:center; gap:12px">
        <span class="pb-time" id="pb-cur-time">00:00</span>
        <input type="range" class="pb-slider" id="pb-slider" min="0" max="1440" value="0">
        <span class="pb-time">24:00</span>
      </div>

      <div style="margin-top:10px; padding:8px 12px; background:var(--bg); border:1px solid var(--border); border-radius:3px; font-size:12px; color:var(--text-lo); font-family:var(--mono)">
        &#9888; ${t('playback.fog_warning')}
      </div>
    </div>

    <div class="pb-layout">
      <div class="pb-map-wrap" id="pb-map-container"></div>

      <!-- Spec §9.7 forensic detail panel — fixed side-panel, hidden by default.
           Populated + shown on event-row / event-map-click; pauses playback;
           closes via [×] or clicking outside. -->
      <aside class="pb-detail" id="pb-detail" style="display:none">
        <header class="pb-detail-head">
          <span id="pb-detail-title"></span>
          <button class="pb-detail-close" data-pb="detail-close" aria-label="${t('playback.detail_close') ?? 'Schließen'}">&times;</button>
        </header>
        <div class="pb-detail-body" id="pb-detail-body"></div>
      </aside>

      <div class="pb-sidebar">
        <div class="panel" style="flex:1;display:flex;flex-direction:column;min-height:0">
          <div style="display:flex; justify-content:space-between; align-items:center;flex-shrink:0;gap:8px;flex-wrap:wrap">
            <div class="panel-title" id="pb-title">${t('playback.event_log', { count: String(allEvents.length) })}</div>
            <div class="pb-filter" role="group" aria-label="${t('playback.filter_group') ?? 'Event-Kategorien'}">
              <button class="pb-filter-btn btn sm active" data-filter="all">${t('playback.filter_all')}</button>
              ${EVENT_CATEGORIES.map(cat =>
                `<button class="pb-filter-btn btn sm" data-filter="${cat}">${t('playback.filter_' + cat) ?? cat}</button>`
              ).join('')}
            </div>
          </div>
          <div style="flex:1;overflow-y:auto;min-height:0" id="pb-log">
            <table class="pb-table dtable">
              <thead><tr><th style="width:28px"></th><th>${t('playback.col_time')}</th><th>${t('playback.col_runner')}</th><th>${t('playback.col_action')}</th><th>${t('playback.col_sector')}</th><th>${t('playback.col_result')}</th><th>${t('playback.col_detail')}</th></tr></thead>
              <tbody id="pb-tbody"></tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  `;

  // Mount the map canvas + runner sidebar
  const mapContainer = container.querySelector('#pb-map-container') as HTMLElement;
  mapContainer.appendChild(pbMap.canvas);

  const sidebar = new RunnerSidebar();
  mapContainer.appendChild(sidebar.el);

  // Entity click → open sidebar with runner details
  pbMap.onEntityClick = (entityId: string) => {
    const runner = resolveEntityToRunner(entityId);
    if (!runner) return;
    const mv = pbMap.getActiveMovement(entityId);
    // Display-only mode: pass ripperdocs so the Cyberware section can
    // show discovered shops, but skip onInstallCyberware — playback is
    // a post-tick forensic view and install is a planning-mode action.
    sidebar.open({
      runner,
      movement: mv,
      tick: currentTick,
      ripperdocs: fixture.player.ripperdocs,
    }, () => {
      // closed
    });
  };

  const slider = container.querySelector('#pb-slider') as HTMLInputElement;
  const curTimeEl = container.querySelector('#pb-cur-time') as HTMLElement;
  const tbody = container.querySelector('#pb-tbody') as HTMLElement;
  const titleEl = container.querySelector('#pb-title') as HTMLElement;

  function getFilteredEvents(): DisplayEvent[] {
    if (filter === 'all') return allEvents;
    return allEvents.filter(e => e.category === filter);
  }

  function renderEventRows(): void {
    const filtered = getFilteredEvents();
    titleEl.textContent = t('playback.event_log', { count: String(filtered.length) });
    const intTick = Math.floor(currentTick);
    // Fog-of-War scope recomputed per render so DTO mutations (new
    // scout, new spy, new contact) take effect immediately.
    const fog = buildFogScope();
    tbody.innerHTML = filtered.map((e, idx) => {
      const temporal = e.tick < intTick ? 'past' : e.tick === intTick ? 'current' : '';
      const fogged = !isEventVisible(e, fog) ? 'pb-row-fogged' : '';
      const cls = [temporal, fogged, 'pb-row-clickable'].filter(Boolean).join(' ');
      const runnerDTO = fixture?.player?.runners?.find(r => r.name === e.runner);
      const miniPortrait = runnerDTO ? portraitHTML(runnerDTO, 20) : '';
      return '<tr class="' + cls + '" data-event-idx="' + idx + '">' +
        '<td style="width:28px;padding:2px">' + miniPortrait + '</td>' +
        '<td style="font-family:var(--mono); font-size:12px; color:var(--text-lo)">' + fmtMin(e.tick) + '</td>' +
        '<td style="font-family:var(--mono); font-size:12px; color:var(--text-mid)">' + e.runner + '</td>' +
        '<td style="font-family:var(--mono); font-size:11px; color:var(--cyan-dim)">' + e.action + '</td>' +
        '<td style="font-family:var(--mono); font-size:11px; color:var(--text-lo)">' + e.sector + '</td>' +
        '<td>' + resultBadge(e.result) + '</td>' +
        '<td style="color:var(--text-mid); font-size:12px">' + e.detail + '</td>' +
        '</tr>';
    }).join('');
    hydratePortraits(tbody);
  }

  function updateTime(): void {
    curTimeEl.textContent = fmtMin(Math.floor(currentTick));
    slider.value = String(Math.floor(currentTick));
    pbMap.setTick(currentTick);
    // Live-update sidebar route/ETA during playback
    if (sidebar.isOpen && sidebar.data?.runner) {
      const eid = resolveRunnerToEntity(sidebar.data.runner);
      if (eid) {
        sidebar.update({ movement: pbMap.getActiveMovement(eid), tick: currentTick });
      }
    }
  }

  /** Reverse lookup: RunnerDTO → entity_id. */
  function resolveRunnerToEntity(runner: RunnerDTO): string | null {
    const runners = fixture?.player?.runners;
    if (!runners) return null;
    const idx = runners.indexOf(runner);
    if (idx < 0) return null;
    // Entity IDs follow pattern p0_hN where N is the runner index
    return `p0_h${idx}`;
  }

  function setPlaying(p: boolean): void {
    playing = p;
    const playBtn = container.querySelector('[data-pb="play"]') as HTMLElement;
    const pauseBtn = container.querySelector('[data-pb="pause"]') as HTMLElement;
    playBtn.classList.toggle('active', playing);
    pauseBtn.classList.toggle('active', !playing);
    if (playing) {
      lastWallTime = performance.now();
      animFrame = requestAnimationFrame(tick);
    } else if (animFrame) {
      cancelAnimationFrame(animFrame);
      animFrame = 0;
    }
  }

  let lastRenderedIntTick = -1;

  function tick(now: number): void {
    if (!playing) return;
    const elapsed = now - lastWallTime;
    lastWallTime = now;
    // 1 game-minute = 500ms wall time at 1x
    const tickDelta = (elapsed / 500) * speed;
    currentTick = Math.min(1440, currentTick + tickDelta);
    if (currentTick >= 1440) {
      currentTick = 1440;
      setPlaying(false);
    }
    updateTime();
    // Only rebuild table DOM when integer tick changes (not every frame)
    const intTick = Math.floor(currentTick);
    if (intTick !== lastRenderedIntTick) {
      lastRenderedIntTick = intTick;
      renderEventRows();
    }
    if (playing) animFrame = requestAnimationFrame(tick);
  }

  // ── Control wiring ──

  slider.addEventListener('input', () => {
    pbMap.resetEvents();
    currentTick = parseInt(slider.value, 10);
    updateTime();
    renderEventRows();
  });

  container.querySelector('[data-pb="play"]')!.addEventListener('click', () => setPlaying(true));
  container.querySelector('[data-pb="pause"]')!.addEventListener('click', () => setPlaying(false));
  container.querySelector('[data-pb="start"]')!.addEventListener('click', () => {
    currentTick = 0;
    setPlaying(false);
    updateTime();
    renderEventRows();
  });
  container.querySelector('[data-pb="end"]')!.addEventListener('click', () => {
    currentTick = 1440;
    setPlaying(false);
    updateTime();
    renderEventRows();
  });

  // Speed buttons
  container.querySelectorAll('.pb-speed').forEach(btn => {
    btn.addEventListener('click', () => {
      speed = parseInt((btn as HTMLElement).dataset.speed!, 10);
      container.querySelectorAll('.pb-speed').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
    });
  });

  // Skip buttons
  container.querySelector('[data-pb="skip-combat"]')!.addEventListener('click', () => {
    const next = allEvents.find(e => e.category === 'kampf' && e.tick > Math.floor(currentTick));
    if (next) {
      currentTick = next.tick;
      setPlaying(false);
      updateTime();
      renderEventRows();
    }
  });
  container.querySelector('[data-pb="skip-fail"]')!.addEventListener('click', () => {
    const next = allEvents.find(e => (e.result === 'FAIL' || e.result === 'CRIT_FAIL' || e.result === 'ARRESTED') && e.tick > Math.floor(currentTick));
    if (next) {
      currentTick = next.tick;
      setPlaying(false);
      updateTime();
      renderEventRows();
    }
  });

  // Filter buttons
  container.querySelectorAll('.pb-filter-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      filter = (btn as HTMLElement).dataset.filter as FilterCategory;
      container.querySelectorAll('.pb-filter-btn').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      renderEventRows();
    });
  });

  // ── Spec §9.7 forensic audit log (v0.42.0) ──────────────────────
  //
  // Clicking an event row opens the detail side-panel with the full
  // record payload (actor, result, cash/heat deltas, and — for combat
  // — attacker/defender/outcome/duration). Playback auto-pauses so the
  // player can read. Clicking another row updates the panel in place;
  // clicking [×] or anywhere outside closes it and resumes playback
  // (if it had been running when the panel opened).
  const detailEl = container.querySelector('#pb-detail') as HTMLElement;
  const detailTitle = container.querySelector('#pb-detail-title') as HTMLElement;
  const detailBody = container.querySelector('#pb-detail-body') as HTMLElement;
  let detailWasPlaying = false;

  function openDetail(e: DisplayEvent): void {
    if (playing) {
      detailWasPlaying = true;
      setPlaying(false);
    }
    detailTitle.textContent = e.action + ' · ' + fmtMin(e.tick);
    detailBody.innerHTML = renderDetailBody(e);
    detailEl.style.display = '';
  }
  function closeDetail(): void {
    detailEl.style.display = 'none';
    if (detailWasPlaying) {
      detailWasPlaying = false;
      setPlaying(true);
    }
  }
  detailEl.querySelector('[data-pb="detail-close"]')?.addEventListener('click', closeDetail);
  // Row delegation — install once on tbody.
  tbody.addEventListener('click', (ev) => {
    const row = (ev.target as HTMLElement).closest('tr[data-event-idx]') as HTMLElement | null;
    if (!row) return;
    const idx = parseInt(row.dataset.eventIdx ?? '-1', 10);
    const filtered = getFilteredEvents();
    if (idx >= 0 && idx < filtered.length) openDetail(filtered[idx]);
  });

  // ── Spec scope — timeline JSON export (v0.42.0) ────────────────
  //
  // Button in the controls row; on click serialises the in-memory
  // TimelineOutput (day + meta + all records) and triggers a browser
  // download as `timeline_day_N.json`. Kept separate from fixture-gen
  // so the player can export whatever is currently loaded.
  container.querySelector('[data-pb="export"]')?.addEventListener('click', () => {
    const blob = new Blob([JSON.stringify(tl, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `timeline_day_${tl.meta.day}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  });

  // Initial render
  renderEventRows();
}

// ── Detail panel body renderer (spec §9.7) ───────────────────────

function renderDetailBody(e: DisplayEvent): string {
  const src = e.source;
  const kv = (k: string, v: string): string =>
    `<div class="pb-detail-kv"><span class="pb-detail-kv-k">${k}</span><span class="pb-detail-kv-v">${v}</span></div>`;
  const section = (label: string, html: string): string =>
    `<div class="pb-detail-section"><div class="pb-detail-label">${label}</div><div class="pb-detail-value">${html}</div></div>`;

  if (e.sourceKind === 'combat') {
    const c = src as CombatRecord;
    const duration = c.tick_end - c.tick_start;
    const outcomeLabel: Record<string, string> = {
      attacker_win: t('playback.outcome_attacker_win') ?? 'Angreifer siegt',
      defender_win: t('playback.outcome_defender_win') ?? 'Verteidiger siegt',
      draw: t('playback.outcome_draw') ?? 'Unentschieden',
    };
    return [
      section(t('playback.detail_when') ?? 'Zeitpunkt',
        kv(t('playback.detail_tick') ?? 'Tick', fmtMin(c.tick_start) + ' – ' + fmtMin(c.tick_end)) +
        kv(t('playback.detail_duration') ?? 'Dauer', duration + ' min')),
      section(t('playback.detail_where') ?? 'Ort',
        kv(t('playback.detail_sector') ?? 'Sektor', e.sector) +
        kv(t('playback.detail_node') ?? 'Knoten', c.node_id)),
      section(t('playback.detail_parties') ?? 'Beteiligte',
        kv(t('playback.detail_attacker') ?? 'Angreifer', resolveRunnerName(c.attacker_id)) +
        kv(t('playback.detail_defender') ?? 'Verteidiger', resolveRunnerName(c.defender_id))),
      section(t('playback.detail_outcome') ?? 'Ergebnis',
        kv(t('playback.detail_result') ?? 'Ausgang', outcomeLabel[c.outcome] ?? c.outcome)),
    ].join('');
  }

  const ev = src as EventRecord;
  const bits: string[] = [];
  bits.push(kv(t('playback.detail_tick') ?? 'Tick', fmtMin(ev.tick)));
  bits.push(kv(t('playback.detail_result') ?? 'Ausgang', ev.result));
  if (ev.delta_cash) bits.push(kv(t('playback.detail_cash') ?? 'Cash', (ev.delta_cash > 0 ? '+' : '') + '$' + Math.round(ev.delta_cash).toLocaleString('de-DE')));
  if (ev.delta_heat) bits.push(kv(t('playback.detail_heat') ?? 'Heat', (ev.delta_heat > 0 ? '+' : '') + String(ev.delta_heat)));
  return [
    section(t('playback.detail_when') ?? 'Zeitpunkt', kv(t('playback.detail_tick') ?? 'Tick', fmtMin(ev.tick))),
    section(t('playback.detail_where') ?? 'Ort',
      kv(t('playback.detail_sector') ?? 'Sektor', e.sector) +
      kv(t('playback.detail_node') ?? 'Knoten', ev.node_id)),
    section(t('playback.detail_actor') ?? 'Akteur',
      kv(t('playback.detail_runner') ?? 'Runner', e.runner) +
      kv(t('playback.detail_action') ?? 'Aktion', ev.event_type) +
      kv(t('playback.detail_category') ?? 'Kategorie', t('playback.filter_' + e.category) ?? e.category)),
    section(t('playback.detail_outcome') ?? 'Ergebnis', bits.join('')),
    ev.detail ? section(t('playback.detail_extra') ?? 'Details', ev.detail) : '',
  ].filter(Boolean).join('');
}

// ── Fog-of-War helper (spec §9.6) ────────────────────────────────
//
// Returns true if the event is visible to the player given their
// current fog-of-war scope:
//   - scoutedSectors — sectors the player has scouted at least once
//   - own-hood coverage — implicit via fixture.player.runners (their
//     CurrentNode sectors are always visible)
//   - contact tiers — CorpSec Tier 1+ reveals arrests, FOB reveals
//     audits, in the contact's sector (from fixture.player.contacts)
//   - spy nodes — from fixture.player.diplomacy.spies, PLACE_SPY
//     targets reveal rival runner movements at the spy's node
//
// Events outside the visible set are dimmed, not removed, so the
// player can still see something-happened markers even if the details
// are private (Spec §9.6: "Kein Omniszienz-Modus — Informationstiefe
// proportional zu investierter Spionage").

interface FogScope {
  visibleSectors: Set<string>;
  visibleNodes: Set<string>;
  corpsecSectors: Set<string>;
  fobSectors: Set<string>;
}

function buildFogScope(): FogScope {
  const player = fixture?.player;
  const visibleSectors = new Set<string>(player?.scoutedSectors ?? []);
  const visibleNodes = new Set<string>();
  const corpsecSectors = new Set<string>();
  const fobSectors = new Set<string>();

  // Own-runner coverage: current-sector of every active runner is visible.
  // RunnerDTO.sector is the sector_id; if missing we fall back to node_id prefix.
  for (const r of player?.runners ?? []) {
    const sector = (r as unknown as { sector?: string; nodeId?: string }).sector
                 ?? extractSector((r as unknown as { nodeId?: string }).nodeId);
    if (sector) visibleSectors.add(sector);
  }
  for (const b of player?.buildings ?? []) {
    if (b.owned && b.nodeId) {
      visibleSectors.add(extractSector(b.nodeId) ?? '');
      visibleNodes.add(b.nodeId);
    }
  }
  // Contact-based expansion (§9.6 Option C)
  for (const c of player?.contacts ?? []) {
    const institution = (c as unknown as { institution?: string }).institution;
    const tier = (c as unknown as { tier?: number }).tier ?? 0;
    const sector = (c as unknown as { sector?: string }).sector;
    if (!institution || tier < 1 || !sector) continue;
    if (institution === 'corpsec') corpsecSectors.add(sector);
    if (institution === 'fob') fobSectors.add(sector);
  }
  // Spy node coverage — each active PLACE_SPY exposes its node.
  for (const s of player?.diplomacy?.spies ?? []) {
    const node = (s as unknown as { nodeId?: string; location?: string }).nodeId
               ?? (s as unknown as { location?: string }).location;
    if (node) visibleNodes.add(node);
  }
  return { visibleSectors, visibleNodes, corpsecSectors, fobSectors };
}

function extractSector(nodeId: string | undefined): string | undefined {
  if (!nodeId) return undefined;
  const m = nodeId.match(/^(S-\d+)/);
  return m ? m[1] : undefined;
}

function isEventVisible(e: DisplayEvent, fog: FogScope): boolean {
  // Direct node visibility (own building, spy node) — always visible.
  if (fog.visibleNodes.has(e.nodeId)) return true;
  // Sector-level visibility — scouted, own buildings' sectors, runner-current sectors.
  if (fog.visibleSectors.has(e.sector)) return true;
  // Category-gated contact expansion
  if (e.category === 'verhaftung' && fog.corpsecSectors.has(e.sector)) return true;
  if (e.category === 'staatlich' && fog.fobSectors.has(e.sector)) return true;
  // Own-actor events are always visible even outside FoW (the player
  // can see their own runners regardless of scouting).
  if (e.source && 'actor' in e.source) {
    const actor = (e.source as EventRecord).actor;
    if (actor && actor.startsWith('p0_')) return true;
  }
  if (e.sourceKind === 'combat') {
    const c = e.source as CombatRecord;
    if (c.attacker_id?.startsWith('p0_') || c.defender_id?.startsWith('p0_')) return true;
  }
  return false;
}
